import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usePost, usePostComments } from '../hooks/usePosts';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageCircle, Share, LogIn } from 'lucide-react';
import CommentSection from '@/components/CommentSection';
import ShareModal from '@/components/ShareModal';
import LikeButton from '@/components/LikeButton';
import { useTranslation } from 'react-i18next';
import { OptimizedImage } from '@/components/ui/optimized-image';
import AppLayout from '@/components/navigation/AppLayout';
import { PostMetaTags } from '@/components/PostMetaTags';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';

const PostDetail = () => {
  const { postId } = useParams<{ postId: string }>();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { data: post, isLoading, error } = usePost(postId);
  const { data: comments, isLoading: isCommentsLoading, refetch: refetchComments } = usePostComments(postId);
  
  // Check if post is private and user is not authenticated
  const isPrivatePost = post && post.privacy_setting !== 'public';
  const canViewPost = !isPrivatePost || (user && post?.user_id === user.id);
  
  // Intersection Observer for video optimization
  useEffect(() => {
    if (!videoRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVideoVisible(true);
            videoRef.current?.play().catch(() => {});
          } else {
            setIsVideoVisible(false);
            videoRef.current?.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    
    observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, []);
  
  const handleCommentAdded = () => {
    refetchComments();
  };
  
  const handleShare = () => {
    setIsShareModalOpen(true);
  };
  
  if (error) {
    return (
      <AppLayout headerTitle={t('titles.postDetail')} showBackButton={true}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-red-500 mb-4">Este post no está disponible</p>
            <Button asChild>
              <Link to="/">Volver al inicio</Link>
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Show error if post is private and user can't view it
  if (post && !canViewPost) {
    return (
      <AppLayout headerTitle={t('titles.postDetail')} showBackButton={true}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold mb-2">Post Privado</h2>
            <p className="text-muted-foreground mb-4">
              Este post no está disponible públicamente. Solo el dueño puede verlo.
            </p>
            <div className="flex gap-2 justify-center">
              <Button asChild>
                <Link to="/">Volver al inicio</Link>
              </Button>
              {!user && (
                <Button asChild variant="outline">
                  <Link to="/auth">
                    <LogIn className="h-4 w-4 mr-2" />
                    Iniciar sesión
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Helper function to determine if media is a video
  const isVideo = (type: string | null) => {
    if (!type) return false;
    return type.includes('video');
  };
  
  // Helper function to determine if media is an image
  const isImage = (type: string | null) => {
    if (!type) return false;
    return type.includes('image');
  };
  
  const mediaUrl = post?.media && post.media.length > 0 ? post.media[0].url : null;
  const mediaType = post?.media && post.media.length > 0 ? post.media[0].media_type : null;

  return (
    <AppLayout headerTitle={t('titles.postDetail')} showBackButton={true}>
      {post && <PostMetaTags post={post} />}
      
      {/* Show login prompt for guests */}
      {!user && post && (
        <Alert className="m-4 bg-blue-50 border-blue-200">
          <AlertDescription className="flex items-center justify-between">
            <span>Inicia sesión para comentar y dar like</span>
            <Button size="sm" asChild>
              <Link to="/auth">
                <LogIn className="h-4 w-4 mr-2" />
                Iniciar sesión
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="flex-1 w-full">
        {isLoading ? (
          <div className="p-4">
            <div className="flex items-center mb-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="ml-3">
                <Skeleton className="h-4 w-24 mb-1" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <Skeleton className="h-64 w-full mb-4" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : post ? (
          <div>
            {/* Post header */}
            <div className="p-4 flex items-center">
              <Link to={`/pet/${post.pet_id}`} className="flex items-center">
                <Avatar className="h-10 w-10 mr-3" key={post.pet?.photo_url || post.pet_id}>
                  {post.pet?.photo_url ? (
                    <AvatarImage 
                      src={post.pet.photo_url} 
                      alt={post.pet?.name || 'Pet'}
                      loading="eager"
                    />
                  ) : (
                    <AvatarFallback>{post.pet?.name?.charAt(0) || '🐾'}</AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <p className="font-medium">
                    {post.pet?.name || 'Unknown Pet'}
                    {post.pet?.is_memorial && (
                      <span className="ml-2 text-xs bg-gray-200 px-1 rounded text-gray-600">
                        In Memory
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {post.created_at
                      ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true })
                      : 'recently'}
                  </p>
                </div>
              </Link>
            </div>

            {/* Post media - Fixed sizing to match Feed behavior */}
            {mediaUrl && (
              <div className="relative w-screen md:w-full left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] md:ml-0 md:mr-0 md:left-0 md:right-0 max-h-[75vh] overflow-hidden bg-gray-100 flex items-center justify-center">
                {isImage(mediaType) ? (
                  <OptimizedImage
                    src={mediaUrl}
                    alt="Post"
                    className="w-full h-auto max-h-[75vh] object-contain"
                    style={{ maxHeight: '75vh' }}
                    priority={true}
                    sizes="(max-width: 768px) 100vw, 800px"
                  />
                ) : isVideo(mediaType) ? (
                  <video
                    ref={videoRef}
                    src={mediaUrl}
                    controls
                    muted
                    playsInline
                    preload="metadata"
                    webkit-playsinline="true"
                    className="w-full h-auto max-h-[75vh] object-contain"
                    style={{ maxHeight: '75vh' }}
                  />
                ) : null}
              </div>
            )}

            {/* Post actions */}
            <div className="px-4 py-3 border-t border-b border-gray-100 flex items-center space-x-3">
              {user ? (
                <LikeButton 
                  id={postId!}
                  showModal={true}
                />
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="flex items-center opacity-50 cursor-not-allowed"
                  disabled
                  title="Inicia sesión para dar like"
                >
                  <span>❤️</span>
                </Button>
              )}
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex items-center"
              >
                <MessageCircle className="h-5 w-5 mr-1" />
                <span>{comments?.length || 0}</span>
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex items-center"
                onClick={handleShare}
              >
                <Share className="h-5 w-5 mr-1" />
                <span>Share</span>
              </Button>
            </div>

            {/* Post caption */}
            {post.caption && (
              <div className="p-4 pb-2">
                <p className="mb-1 font-medium">{post.pet?.name || 'Unknown Pet'}</p>
                <p>{post.caption}</p>
              </div>
            )}

            {/* Comments */}
            <div className="p-4 pt-2">
              <h3 className="font-medium mb-4">Comments</h3>
              
              {isCommentsLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex mb-4">
                    <Skeleton className="h-8 w-8 rounded-full mr-3" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-24 mb-1" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  </div>
                ))
              ) : (
                <CommentSection 
                  comments={comments || []} 
                  postId={postId || ''}
                  onCommentAdded={handleCommentAdded}
                  disabled={!user}
                />
              )}
              
              {!user && (
                <div className="text-center text-sm text-muted-foreground mt-4">
                  <Link to="/auth" className="text-primary hover:underline">
                    Inicia sesión
                  </Link>
                  {' '}para dejar un comentario
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Share Modal */}
      {post && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          postId={postId!}
          petName={post.pet?.name}
        />
      )}
    </AppLayout>
  );
};

export default PostDetail;
